import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Upload, FileText, Trash2, Plus, CalendarDays, Users as UsersIcon, MapPin,
} from 'lucide-react';

/**
 * CourseEditExtras — appears inside the course edit modal for existing courses.
 * Provides:
 *   - Programme PDF upload/replace/delete
 *   - Inline session management (onsite courses only)
 */
export const CourseEditExtras = ({ course, onCourseChanged }) => {
  if (!course?.id) return null;
  return (
    <div className="space-y-6 pt-6 mt-6 border-t border-dashed border-[#1F2A4A]/15">
      <ProgrammePdfSection course={course} onChanged={onCourseChanged} />
      {course.course_type === 'onsite' && (
        <SessionsInlineManager course={course} />
      )}
    </div>
  );
};

// ─────────────────────────────────────────── Programme PDF
const ProgrammePdfSection = ({ course, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const hasPdf = !!course.programme_pdf_path;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Seul un fichier PDF est accepté');
      return;
    }
    setUploading(true);
    try {
      await api.uploadProgrammePdf(course.id, file);
      toast.success('Programme PDF mis en ligne');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer le programme PDF de cette formation ?')) return;
    try {
      await api.deleteProgrammePdf(course.id);
      toast.success('Programme PDF supprimé');
      onChanged();
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="course-programme-pdf-section">
      <div className="flex items-baseline justify-between mb-3">
        <Label className="text-xs uppercase tracking-[0.25em] text-[#C9B896]">
          Programme PDF (téléchargement avec capture de lead)
        </Label>
        {hasPdf && (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
            En ligne
          </Badge>
        )}
      </div>

      {hasPdf ? (
        <div className="bg-[#FAF7F0]/70 border border-[#1F2A4A]/10 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C9B896]/20 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-[#1F2A4A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1F2A4A] truncate">
              {course.programme_pdf_name || 'programme.pdf'}
            </p>
            <p className="text-xs text-muted-foreground">
              Les prospects pourront le télécharger après avoir renseigné leurs coordonnées.
            </p>
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" className="hidden" onChange={handleUpload} data-testid="course-programme-replace" />
            <span className="inline-flex items-center gap-1.5 text-xs text-[#1F2A4A] border border-[#1F2A4A]/15 rounded-full px-3 py-1.5 hover:bg-white">
              <Upload className="w-3 h-3" /> Remplacer
            </span>
          </label>
          <button
            type="button"
            onClick={handleDelete}
            className="text-red-500/70 hover:text-red-600 p-1.5"
            data-testid="course-programme-delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="block cursor-pointer">
          <input
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleUpload}
            data-testid="course-programme-upload"
          />
          <div className="bg-[#FAF7F0]/40 border-2 border-dashed border-[#1F2A4A]/15 hover:border-[#C9B896] hover:bg-[#C9B896]/5 rounded-xl p-6 text-center transition-colors">
            <Upload className="w-6 h-6 text-[#C9B896] mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1F2A4A]">
              {uploading ? 'Upload en cours…' : 'Uploader le programme PDF'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Max 20 Mo — PDF uniquement</p>
          </div>
        </label>
      )}
    </div>
  );
};

// ─────────────────────────────────────────── Sessions
const SessionsInlineManager = ({ course }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    start_date: '', end_date: '', available_spots: course.max_participants || 3,
    location: course.location || 'Istres',
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getSessions(course.id);
      setSessions(data);
    } catch (e) { toast.error('Erreur de chargement des sessions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [course.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error('Dates obligatoires');
    if (parseISO(form.end_date) < parseISO(form.start_date)) return toast.error('Date de fin avant la date de début');
    try {
      await api.createSession({
        course_id: course.id,
        start_date: form.start_date,
        end_date: form.end_date,
        available_spots: parseInt(form.available_spots) || 1,
        location: form.location,
      });
      toast.success('Session ajoutée');
      setShowAdd(false);
      setForm({ ...form, start_date: '', end_date: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await api.deleteSession(id);
      toast.success('Session supprimée');
      load();
    } catch (err) { toast.error('Erreur'); }
  };

  const future = sessions
    .filter((s) => parseISO(s.start_date) > new Date())
    .sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date));
  const past = sessions
    .filter((s) => parseISO(s.start_date) <= new Date())
    .sort((a, b) => parseISO(b.start_date) - parseISO(a.start_date));

  return (
    <div data-testid="course-sessions-section">
      <div className="flex items-baseline justify-between mb-3">
        <Label className="text-xs uppercase tracking-[0.25em] text-[#C9B896]">
          Sessions à venir
        </Label>
        <Button
          type="button" size="sm" variant="ghost"
          onClick={() => setShowAdd(!showAdd)}
          className="text-[#C9B896]"
          data-testid="add-session-toggle"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          {showAdd ? 'Annuler' : 'Nouvelle session'}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-[#FAF7F0]/70 border border-[#1F2A4A]/10 rounded-xl p-4 mb-3">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5568]">Début</Label>
              <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="session-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5568]">Fin</Label>
              <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="session-end" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5568]">Places</Label>
              <Input type="number" min="1" value={form.available_spots} onChange={(e) => setForm({ ...form, available_spots: e.target.value })} data-testid="session-spots" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5568]">Lieu</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="session-location" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={submit} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="session-save">
              Ajouter la session
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground italic py-2">Chargement…</div>
      ) : future.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">Aucune session à venir.</p>
      ) : (
        <ul className="space-y-2">
          {future.map((s) => (
            <li key={s.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-3 flex items-center gap-3" data-testid={`session-row-${s.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center flex-shrink-0">
                <CalendarDays className="w-5 h-5 text-[#1F2A4A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1F2A4A]">
                  Du {format(parseISO(s.start_date), 'dd MMM yyyy', { locale: fr })}
                  {' au '}
                  {format(parseISO(s.end_date), 'dd MMM yyyy', { locale: fr })}
                </p>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.location}</span>
                  <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {s.enrolled_count}/{s.available_spots} places</span>
                </div>
              </div>
              <button
                type="button" onClick={() => remove(s.id)}
                className="text-red-500/70 hover:text-red-600 p-1.5"
                data-testid={`session-delete-${s.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {past.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            + {past.length} session{past.length > 1 ? 's' : ''} passée{past.length > 1 ? 's' : ''}
          </summary>
          <ul className="mt-2 space-y-1">
            {past.slice(0, 5).map((s) => (
              <li key={s.id} className="text-xs text-muted-foreground pl-3 border-l-2 border-[#1F2A4A]/10">
                {format(parseISO(s.start_date), 'dd MMM yyyy', { locale: fr })} — {s.enrolled_count} inscrits
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
